import { Folder, Sun, Moon } from 'lucide-react'
import { useEffect, useState } from 'react'
import CEFRBadge from '../components/shared/CEFRBadge'
import DeckCard from '../components/shared/DeckCard'
import DeckCover from '../components/shared/DeckCover'
import DeckProgressBar from '../components/shared/DeckProgressBar'
import DeckStatusBadge, { type DeckStatus } from '../components/shared/DeckStatusBadge'
import MasteryMeter from '../components/shared/MasteryMeter'

// ── Helpers ───────────────────────────────────────────────────────────────────

type Theme = 'light' | 'dark'

function getTheme(): Theme {
  const t = localStorage.getItem('cs-theme')
  if (t === 'light' || t === 'dark') return t
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 48 }}>
      <h2
        style={{
          fontFamily: 'var(--font-ui)',
          fontSize: 14,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--text-secondary)',
          marginBottom: 20,
          paddingBottom: 8,
          borderBottom: '1px solid var(--border)',
        }}
      >
        {title}
      </h2>
      {children}
    </section>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 14 }}>
      <span
        style={{
          fontFamily: 'var(--font-ui)',
          fontSize: 12,
          color: 'var(--text-secondary)',
          width: 100,
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {children}
      </div>
    </div>
  )
}

// Deterministic fake UUIDs — one per tint slot (last 8 hex chars → index 0..7).
// Values chosen so `parseInt(hex, 16) % 8 === N` for tint N.
const TINT_DECK_IDS = [
  '00000000-0000-0000-0000-000000000000', // → 0
  '00000000-0000-0000-0000-000000000001', // → 1
  '00000000-0000-0000-0000-000000000002', // → 2
  '00000000-0000-0000-0000-000000000003', // → 3
  '00000000-0000-0000-0000-000000000004', // → 4
  '00000000-0000-0000-0000-000000000005', // → 5
  '00000000-0000-0000-0000-000000000006', // → 6
  '00000000-0000-0000-0000-000000000007', // → 7
]

const SYSTEM_CATEGORIES = [
  'vocabulary', 'business', 'travel', 'food',
  'arts', 'science', 'technology', 'sports',
]

const STATUS_ROLES: DeckStatus[] = ['paused', 'readonly', 'public', 'system']

// ── Demo page ─────────────────────────────────────────────────────────────────

/**
 * Internal design-system demo page — primitives only, no production wiring.
 * Route: /design-demo (public, no auth required).
 * Use the theme toggle in the header to verify both themes.
 */
export default function DesignDemoPage() {
  const [theme, setTheme] = useState<Theme>(getTheme)

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem('cs-theme', theme)
  }, [theme])

  const toggleTheme = () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg-page)',
        color: 'var(--text-primary)',
        fontFamily: 'var(--font-ui)',
      }}
    >
      {/* ── Header ── */}
      <div
        style={{
          background: 'var(--bg-header)',
          borderBottom: '1px solid var(--border-on-dark)',
          padding: '0 32px',
          height: 56,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span
          style={{
            color: 'rgba(255,255,255,0.9)',
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '0.04em',
          }}
        >
          Design Primitives — Wave 3 / 4
        </span>
        <button
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'rgba(255,255,255,0.7)',
            padding: 8,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            minWidth: 44,
            minHeight: 44,
            justifyContent: 'center',
          }}
        >
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </button>
      </div>

      {/* ── Content ── */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 32px' }}>

        {/* ── 1. Deck Cover ── */}
        <Section title="Deck Cover (Wave 4 §2)">

          <Row label="All 8 tints — user deck (initial letter)">
            {TINT_DECK_IDS.map((id, i) => (
              <DeckCover key={id} deckId={id} title={`Tint${i}`} isSystem={false} size="catalog" />
            ))}
          </Row>

          <Row label="All 8 tints — system deck (category icon)">
            {TINT_DECK_IDS.map((id, i) => (
              <DeckCover
                key={id}
                deckId={id}
                title={SYSTEM_CATEGORIES[i]}
                isSystem
                category={SYSTEM_CATEGORIES[i]}
                size="catalog"
              />
            ))}
          </Row>

          <Row label="Three sizes (tint 0)">
            <DeckCover deckId={TINT_DECK_IDS[0]} title="Vocabulary" isSystem category="vocabulary" size="list" />
            <DeckCover deckId={TINT_DECK_IDS[0]} title="Vocabulary" isSystem category="vocabulary" size="catalog" />
            <DeckCover deckId={TINT_DECK_IDS[0]} title="Vocabulary" isSystem category="vocabulary" size="header" />
            <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>list / catalog / header</span>
          </Row>

          <Row label="User deck sizes">
            <DeckCover deckId={TINT_DECK_IDS[3]} title="My Words" size="list" />
            <DeckCover deckId={TINT_DECK_IDS[3]} title="My Words" size="catalog" />
            <DeckCover deckId={TINT_DECK_IDS[3]} title="My Words" size="header" />
          </Row>

          <Row label="Long title truncates">
            <DeckCover deckId={TINT_DECK_IDS[6]} title=" English Vocabulary A1" size="catalog" />
            <DeckCover deckId={TINT_DECK_IDS[7]} title="1984" size="catalog" />
          </Row>
        </Section>

        {/* ── 2. Status Badges ── */}
        <Section title="Status Badge (Wave 4 §4)">

          <Row label="All statuses — sm">
            {STATUS_ROLES.map((s) => (
              <DeckStatusBadge key={s} status={s} size="sm" />
            ))}
          </Row>

          <Row label="All statuses — md">
            {STATUS_ROLES.map((s) => (
              <DeckStatusBadge key={s} status={s} size="md" />
            ))}
          </Row>

          <Row label="Icon-only (sm / md)">
            {STATUS_ROLES.map((s) => (
              <DeckStatusBadge key={`${s}-icon`} status={s} size="sm" iconOnly />
            ))}
            {STATUS_ROLES.map((s) => (
              <DeckStatusBadge key={`${s}-icon-md`} status={s} size="md" iconOnly />
            ))}
          </Row>
        </Section>

        {/* ── 3. Mastery Meter (per-word, gap-separated segments) ── */}
        <Section title="Mastery Meter — per-word, gap-separated segments (Wave 3 §1 amendment 2026-07-05)">

          <Row label="Levels 0–8 (36px)">
            {Array.from({ length: 9 }, (_, i) => i).map((lvl) => (
              <div key={lvl} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <MasteryMeter level={lvl === 0 ? null : lvl} width={36} />
                <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{lvl}</span>
              </div>
            ))}
          </Row>

          <Row label="Wide track (64px)">
            {[0, 1, 2, 4, 6, 8].map((lvl) => (
              <div key={lvl} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <MasteryMeter level={lvl === 0 ? null : lvl} width={64} />
                <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{lvl}</span>
              </div>
            ))}
          </Row>

          <p
            style={{
              fontSize: 12,
              color: 'var(--text-secondary)',
              marginTop: 4,
              fontStyle: 'italic',
            }}
          >
            Eight discrete gap-separated segments — countable at a glance at row size.
            Level 0 = new/unseen (all empty). Fill is calm/cool at low levels (never red), shifts toward accent-confident at high levels.
          </p>
        </Section>

        {/* ── 4. Deck Progress Hairline (continuous) ── */}
        <Section title="Deck Progress Hairline — continuous, no ticks (Wave 3 §1)">

          <div style={{ maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              { learned: 0,  total: 20, label: '0 / 20 — empty'    },
              { learned: 3,  total: 20, label: '3 / 20 — 15%'      },
              { learned: 10, total: 20, label: '10 / 20 — 50%'     },
              { learned: 17, total: 20, label: '17 / 20 — 85%'     },
              { learned: 20, total: 20, label: '20 / 20 — complete' },
            ].map(({ learned, total, label }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <DeckProgressBar learned={learned} total={total} width={240} />
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{label}</span>
              </div>
            ))}
          </div>

          <p
            style={{
              fontSize: 12,
              color: 'var(--text-secondary)',
              marginTop: 16,
              fontStyle: 'italic',
            }}
          >
            Smooth fill — no ticks. Deck-level bars encode a ratio, not a discrete level position.
            Fill always renders in --mastery-fill-high ("earned").
          </p>
        </Section>

        {/* ── 5. Deck Card — shared component, Mine + Catalog variants ── */}
        <Section title="Deck Card — shared component (Wave 4)">

          <p style={{ fontSize: 12, color: 'var(--warning)', fontStyle: 'italic', margin: '0 0 20px', padding: '8px 12px', background: 'color-mix(in srgb, var(--warning) 8%, transparent)', borderRadius: 8, border: '1px solid color-mix(in srgb, var(--warning) 20%, transparent)' }}>
            Each variant is rendered inside a faithful copy of its real live wrapper (flex column, gap 8, maxWidth 680).
            A shared grid was the previous oracle — it equalised card width and masked the container divergence.
            On-device screenshots of /library at the same viewport are the only authoritative parity check.
          </p>

          {/* Mine wrapper — faithful copy: flex column, gap 8, maxWidth 680 */}
          <div style={{ marginBottom: 24 }}>
            <p style={{ margin: '0 0 10px', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)' }}>
              Mine — flex column · gap 8 · maxWidth 680
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 680 }}>

              {/* Mine variant — user deck, progress bar, Learn action, status badge */}
              <DeckCard
                deckId={TINT_DECK_IDS[2]}
                title="My Imported Words"
                cardCount={120}
                isSystem={false}
                actionLabel="Learn"
                onAction={() => {}}
                metaSlot={
                  <>
                    <span aria-hidden="true">·</span>
                    <span style={{ color: 'var(--warning)', fontWeight: 600 }}>5 due</span>
                    <span aria-hidden="true">·</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      <Folder size={11} strokeWidth={1.75} aria-hidden="true" />
                      Travel
                    </span>
                  </>
                }
                belowHeaderSlot={
                  <div style={{ marginTop: 12 }}>
                    <DeckProgressBar learned={42} total={120} width="100%" height={3} />
                  </div>
                }
                bottomLeftSlot={<DeckStatusBadge status="paused" size="sm" />}
              />

              {/* Mine variant — system deck */}
              <DeckCard
                deckId={TINT_DECK_IDS[0]}
                title="English A1 Core Vocabulary"
                cardCount={80}
                isSystem
                category="vocabulary"
                actionLabel="Learn"
                onAction={() => {}}
                metaSlot={
                  <>
                    <span aria-hidden="true">·</span>
                    <span style={{ color: 'var(--warning)', fontWeight: 600 }}>12 due</span>
                  </>
                }
                belowHeaderSlot={
                  <div style={{ marginTop: 12 }}>
                    <DeckProgressBar learned={30} total={80} width="100%" height={3} />
                  </div>
                }
              />

            </div>
          </div>

          {/* Catalog wrapper — same: flex column, gap 8, maxWidth 680 */}
          <div style={{ marginBottom: 20 }}>
            <p style={{ margin: '0 0 10px', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)' }}>
              Catalog — same wrapper: flex column · gap 8 · maxWidth 680
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 680 }}>

              {/* Catalog variant — CEFR badge, description, hairline, Browse action */}
              <DeckCard
                deckId={TINT_DECK_IDS[5]}
                title="Business English — Meetings & Negotiations"
                cardCount={64}
                isSystem
                category="business"
                hoverShadow
                actionLabel="Browse"
                onAction={() => {}}
                metaSlot={
                  <>
                    <span aria-hidden="true">·</span>
                    <CEFRBadge level="B2" />
                  </>
                }
                belowHeaderSlot={
                  <>
                    <p
                      style={{
                        margin: '8px 0 0',
                        fontSize: 13,
                        fontFamily: 'var(--font-ui)',
                        color: 'var(--text-secondary)',
                        lineHeight: 1.5,
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      Vocabulary for meetings, negotiations, and professional correspondence.
                    </p>
                    <div style={{ marginTop: 12 }}>
                      <DeckProgressBar learned={18} total={64} width="100%" height={3} />
                    </div>
                  </>
                }
              />

              {/* Catalog variant — paused badge + hairline */}
              <DeckCard
                deckId={TINT_DECK_IDS[7]}
                title="Travel Phrases"
                cardCount={30}
                isSystem
                category="travel"
                hoverShadow
                actionLabel="Browse"
                onAction={() => {}}
                metaSlot={
                  <>
                    <span aria-hidden="true">·</span>
                    <CEFRBadge level="A1" />
                  </>
                }
                belowHeaderSlot={
                  <div style={{ marginTop: 12 }}>
                    <DeckProgressBar learned={0} total={30} width="100%" height={3} />
                  </div>
                }
                bottomLeftSlot={<DeckStatusBadge status="paused" size="sm" />}
              />

            </div>
          </div>

          <p style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic', margin: '4px 0 0' }}>
            Mine and Catalog now share the same wrapper (flex column, gap 8, maxWidth 680) — cards are identical in physical width at every breakpoint.
            They differ in slots only: Mine gets due count + folder + Learn; Catalog gets description + CEFR + Browse.
            Status badge (Not in daily) appears on both surfaces when is_active_for_learning is false.
          </p>
        </Section>

        {/* ── 6. Composed example: deck row fragment ── */}
        <Section title="Composed — deck list row fragment">
          {[
            { id: TINT_DECK_IDS[0], title: 'English A1 Core', system: true, cat: 'vocabulary', status: null as DeckStatus | null,  learned: 42, total: 80,  mastery: 5 },
            { id: TINT_DECK_IDS[3], title: 'My Imported Words',system: false, cat: '',          status: 'paused' as DeckStatus,    learned: 7,  total: 120, mastery: 2 },
            { id: TINT_DECK_IDS[6], title: 'Business English',  system: true,  cat: 'business',  status: 'system' as DeckStatus,   learned: 18, total: 40,  mastery: 7 },
            { id: TINT_DECK_IDS[2], title: 'Travel Phrases',    system: false, cat: '',          status: 'public' as DeckStatus,   learned: 0,  total: 30,  mastery: 1 },
          ].map((d) => (
            <div
              key={d.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 16px',
                background: 'var(--bg-surface)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
                marginBottom: 8,
              }}
            >
              <DeckCover deckId={d.id} title={d.title} isSystem={d.system} category={d.cat} size="list" />

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {d.title}
                  </span>
                  {d.status && <DeckStatusBadge status={d.status} size="sm" />}
                </div>
                <DeckProgressBar learned={d.learned} total={d.total} />
                <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>
                  {d.learned} / {d.total} cards learned
                </span>
              </div>

              <MasteryMeter level={d.mastery} width={36} />
            </div>
          ))}
        </Section>

      </div>
    </div>
  )
}
